/**
 * HUSETS MANNSKAP OG VAKTPLAN (sak 229, agent V3, 0448).
 *
 * «Personer» på et spillested er husets mannskap: folkene huset kan sette på vakt, uten årgang.
 * Vaktplanen leser vaktene på arrangementene i en periode, på tvers av husets årganger, gruppert per
 * arrangement. Å sette folk på en vakt er `PUT /shifts/:id/assignees` som før — ingen ny vei inn.
 *
 * Årgangen står i vaktenes `editionId` fordi `Shift` er den samme formen som overalt ellers; den
 * vises aldri i husets brukerflate.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';

import { booleanQuerySchema } from './common.js';
import { shiftDtoSchema, shiftPrioritySchema } from './people.js';
import { venueNightStatusSchema } from './venues.js';

/* ------------------------------------------------------------------------------------ mannskap */

export const VENUE_CREW_SORT_KEYS = ['name'] as const;
export type VenueCrewSortKey = (typeof VENUE_CREW_SORT_KEYS)[number];

const noteSchema = z.string().trim().max(2000);

export const venueCrewMemberDtoSchema = z
  .object({
    id: z.uuid(),
    festivalId: z.uuid(),
    personId: z.uuid(),
    /** Fra kontaktregisteret; null når kontakten er fjernet derfra. */
    displayName: z.string().nullable(),
    contactId: z.uuid().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    staffingRoleId: z.uuid().nullable(),
    staffingRoleName: z.string().nullable(),
    note: z.string().nullable(),
    /** Kommende vakter personen står på i huset (ikke takket nei). */
    upcomingShifts: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'VenueCrewMember' });

export const listVenueCrewQuerySchema = cursorQuerySchema(VENUE_CREW_SORT_KEYS)
  .extend({ festivalId: z.uuid() })
  .meta({ id: 'ListVenueCrewQuery' });

export const venueCrewListResponseSchema = page(venueCrewMemberDtoSchema, 'VenueCrewListResponse');

export const addVenueCrewMemberRequestSchema = z
  .object({
    festivalId: z.uuid(),
    personId: z.uuid(),
    staffingRoleId: z.uuid().nullish(),
    note: noteSchema.nullish(),
  })
  .meta({ id: 'AddVenueCrewMemberRequest' });

export const updateVenueCrewMemberRequestSchema = z
  .object({
    staffingRoleId: z.uuid().nullish(),
    note: noteSchema.nullish(),
  })
  .meta({ id: 'UpdateVenueCrewMemberRequest' });

/** Husets poster, én per nøkkel (den nyeste årgangens), til velgeren på mannskapet og vakta. */
export const venueCrewPostSchema = z
  .object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    departmentName: z.string().nullable(),
  })
  .meta({ id: 'VenueCrewPost' });

export const venueCrewPostsQuerySchema = z
  .object({ festivalId: z.uuid() })
  .meta({ id: 'VenueCrewPostsQuery' });

export const venueCrewPostListSchema = z
  .object({ items: z.array(venueCrewPostSchema) })
  .meta({ id: 'VenueCrewPostList' });

/* ------------------------------------------------------------------------------- husets poster */

/**
 * SPILLESTEDETS POSTER SOM EN EGEN SIDE (F39): Lydtekniker, Bar, Vakt. Spillestedet lager, gir nytt
 * navn til og sletter postene selv; malen på salen og vaktene på arrangementene velger herfra.
 */
export const venuePostSchema = z
  .object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    defaultNeeded: z.number().int(),
    sortOrder: z.number().int(),
    /** Linjer i salenes mannskapsmal som bruker posten. */
    templateCount: z.number().int(),
    /** Kommende vakter (fra i dag) på posten. */
    upcomingShiftCount: z.number().int(),
  })
  .meta({ id: 'VenuePost' });

export const venuePostListSchema = z
  .object({ items: z.array(venuePostSchema) })
  .meta({ id: 'VenuePostList' });

export const createVenuePostRequestSchema = z
  .object({
    festivalId: z.uuid(),
    name: z.string().trim().min(1).max(120),
    defaultNeeded: z.number().int().min(1).max(500).optional(),
  })
  .meta({ id: 'CreateVenuePostRequest' });

export const updateVenuePostRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    defaultNeeded: z.number().int().min(1).max(500).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateVenuePostRequest' });

/* ------------------------------------------------------------------------------------ vaktplan */

/** Det lengste vinduet planen leses gjennom. En uke er det vanlige; en måned er panelets. */
export const VENUE_PLAN_MAX_DAYS = 62;
/** Flere vakter enn dette i ett vindu er ikke en plan noen leser — svaret sier at det ble kuttet. */
export const VENUE_PLAN_MAX_SHIFTS = 500;

const DAY_MS = 86_400_000;

export const venueCrewPlanQuerySchema = z
  .object({
    festivalId: z.uuid(),
    /** Første og siste dag, begge med. Utelatt: i dag og seks dager fram. */
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    /** «Bare det som mangler folk»: vakter med færre enn behovet, på arrangementer som ikke er avlyst. */
    onlyGaps: booleanQuerySchema.optional(),
    /** Ett arrangement — fanen Vakter. Da gjelder ikke perioden. */
    nightId: z.uuid().optional(),
  })
  .refine(
    (value) =>
      value.from === undefined ||
      value.to === undefined ||
      (Date.parse(value.to) >= Date.parse(value.from) &&
        Date.parse(value.to) - Date.parse(value.from) < VENUE_PLAN_MAX_DAYS * DAY_MS),
    { message: `The window must run forward, at most ${VENUE_PLAN_MAX_DAYS} days.`, path: ['to'] },
  )
  .meta({ id: 'VenueCrewPlanQuery' });

export const venueCrewPlanEventSchema = z
  .object({
    nightId: z.uuid(),
    nightOn: z.iso.date(),
    title: z.string().nullable(),
    artistName: z.string().nullable(),
    promoterName: z.string().nullable(),
    roomName: z.string(),
    status: venueNightStatusSchema,
    /** Arrangementets sone — vaktenes klokkeslett vises i den. */
    timezone: z.string(),
    doorsTime: z.string().nullable(),
    needed: z.number().int().nonnegative(),
    staffed: z.number().int().nonnegative(),
    /** Summen av det som mangler på vaktene, aldri negativ per vakt. */
    missing: z.number().int().nonnegative(),
    /** Vaktene som mangler folk først, så i tidsrekkefølge. */
    shifts: z.array(shiftDtoSchema),
  })
  .meta({ id: 'VenueCrewPlanEvent' });

export const venueCrewPlanSchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    events: z.array(venueCrewPlanEventSchema),
    /** Vinduet hadde flere vakter enn `VENUE_PLAN_MAX_SHIFTS`; resten er ikke med. */
    truncated: z.boolean(),
    /**
     * FUNN 39: datoen til nærmeste arrangement før og etter vinduet, så vaktplanen kan hoppe dit i stedet for
     * at man blar uke for uke. Null når det ikke finnes noe.
     */
    previousNightOn: z.iso.date().nullable().optional(),
    nextNightOn: z.iso.date().nullable().optional(),
  })
  .meta({ id: 'VenueCrewPlan' });

/** Én ekstra vakt på et arrangement. Posten oversettes til arrangementets egen årgang av serveren. */
export const createVenueNightShiftRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(120),
    staffingRoleId: z.uuid().nullish(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    needed: z.number().int().min(1).max(500).default(1),
    priority: shiftPrioritySchema.optional(),
  })
  .refine((value) => value.endsAt > value.startsAt, {
    message: 'A shift must end after it starts.',
    path: ['endsAt'],
  })
  .meta({ id: 'CreateVenueNightShiftRequest' });

export type VenueCrewMemberDto = z.infer<typeof venueCrewMemberDtoSchema>;
export type ListVenueCrewQuery = z.infer<typeof listVenueCrewQuerySchema>;
export type VenueCrewListResponse = z.infer<typeof venueCrewListResponseSchema>;
export type AddVenueCrewMemberRequest = z.infer<typeof addVenueCrewMemberRequestSchema>;
export type UpdateVenueCrewMemberRequest = z.infer<typeof updateVenueCrewMemberRequestSchema>;
export type VenueCrewPost = z.infer<typeof venueCrewPostSchema>;
export type VenueCrewPostsQuery = z.infer<typeof venueCrewPostsQuerySchema>;
export type VenueCrewPostList = z.infer<typeof venueCrewPostListSchema>;
export type VenuePost = z.infer<typeof venuePostSchema>;
export type VenuePostList = z.infer<typeof venuePostListSchema>;
export type CreateVenuePostRequest = z.infer<typeof createVenuePostRequestSchema>;
export type UpdateVenuePostRequest = z.infer<typeof updateVenuePostRequestSchema>;
export type VenueCrewPlanQuery = z.infer<typeof venueCrewPlanQuerySchema>;
export type VenueCrewPlanEvent = z.infer<typeof venueCrewPlanEventSchema>;
export type VenueCrewPlan = z.infer<typeof venueCrewPlanSchema>;
export type CreateVenueNightShiftRequest = z.infer<typeof createVenueNightShiftRequestSchema>;
